import React, { useState } from 'react';
import styled from 'styled-components';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { Button } from '@/common/components/button/Button';
import { Input } from '@/components/ui/Input';
import { SignUpData } from '../SignUp';
import { commonService } from '@/services/commonService';
import { toastUtils } from '@/common/utils/toastUtils';
import { isValidEmail } from '@/common/utils/textFormat';

/**
 * 3단계: 회원가입 정보 입력 컴포넌트
 */

interface FormStepProps {
  data: SignUpData;
  onNext: (data: Partial<SignUpData>) => void;
  onPrev: () => void;
}

interface FormErrors {
  userId?: string;
  email?: string;
  password?: string;
  passwordConfirm?: string;
}

interface PasswordValidation {
  length: boolean;
  hasLetter: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
  isValid: boolean;
}

const FormStep: React.FC<FormStepProps> = ({ data, onNext, onPrev }) => {
  const [formData, setFormData] = useState({
    userId: data.userId,
    email: data.email || '',
    password: data.password,
    passwordConfirm: data.passwordConfirm,
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUserIdChecked, setIsUserIdChecked] = useState(false);
  const [isEmailChecked, setIsEmailChecked] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
    length: false,
    hasLetter: false,
    hasNumber: false,
    hasSpecial: false,
    isValid: false,
  });

  /**
   * 아이디 유효성 검증 (영문 필수, 숫자 선택, 특수문자 불허)
   */
  const validateUserId = (userId: string): string | undefined => {
    if (!userId.trim()) {
      return '아이디를 입력해주세요.';
    }
    if (userId.length < 4) {
      return '아이디는 4자 이상이어야 합니다.';
    }
    if (userId.length > 20) {
      return '아이디는 20자 이하여야 합니다.';
    }
    
    const hasLetter = /[a-zA-Z]/.test(userId);
    const isValidFormat = /^[a-zA-Z0-9]+$/.test(userId);
    
    if (!isValidFormat) {
      return '아이디는 영문과 숫자만 사용할 수 있습니다.';
    }
    if (!hasLetter) {
      return '아이디는 영문을 반드시 포함해야 합니다.';
    }
    
    return undefined;
  };

  /**
   * 비밀번호 실시간 유효성 검사 (영문, 숫자, 특수문자 중 2가지 이상)
   */
  const validatePasswordRealtime = (password: string): PasswordValidation => {
    const length = password.length >= 8;
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    
    const validTypes = [hasLetter, hasNumber, hasSpecial].filter(Boolean).length;
    const isValid = length && validTypes >= 2;

    return {
      length,
      hasLetter,
      hasNumber,
      hasSpecial,
      isValid,
    };
  };

  /**
   * 입력 필드 변경 핸들러
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    if(name === 'userId') {
      const userIdError = validateUserId(value);
      setErrors(prev => ({
        ...prev,
        userId: userIdError,
      }));
      setIsUserIdChecked(false);
    }
    
    if(name === 'email') {
      setErrors(prev => ({
        ...prev,
        email: isValidEmail(value) ? undefined : '이메일 형식이 올바르지 않습니다.',
      }));
      setIsEmailChecked(false);
    }
    
    // 비밀번호 실시간 검증
    if (name === 'password') {
      const validation = validatePasswordRealtime(value);
      setPasswordValidation(validation);
      setErrors(prev => ({
        ...prev,
        password: validation.isValid ? undefined : '영문, 숫자, 특수문자 중 2가지 이상 조합 (8자 이상)',
      }));
    }

    if(name === 'passwordConfirm') {
      setErrors(prev => ({
        ...prev,
        passwordConfirm: value === formData.password || value.length === 0 ? undefined : '비밀번호가 일치하지 않습니다.',
      }));
    }
  };

  /**
   * 아이디 중복 확인
   */
  const handleCheckUserId = async () => {
    if(isUserIdChecked) return;

    const userIdError = validateUserId(formData.userId);
    if (userIdError) {
      setErrors(prev => ({ ...prev, userId: userIdError }));
      return;
    }

    try {
      setIsLoading(true);
      const result = await commonService.checkDuplicateValue('loginId', formData.userId);

      if(result.exists){
        setErrors(prev => ({ ...prev, userId: '이미 사용 중인 아이디입니다.' }));
        setIsLoading(false);
        setIsUserIdChecked(false);
        return;
      }
      toastUtils.success('사용 가능한 아이디입니다.');
      setIsLoading(false);
      setIsUserIdChecked(true);
    } catch (error) {
      toastUtils.error('아이디 중복 검사 중 오류가 발생했습니다. 다시 시도해주세요.');
      setIsLoading(false);
      setIsUserIdChecked(false);
    }
  };

  /**
   * 이메일 중복 확인
   */
  const handleCheckEmail = async () => {
    if(isEmailChecked) return;

    if (!formData.email.trim()) {
      setErrors(prev => ({ ...prev, email: '이메일을 입력해주세요.' }));
      return;
    }

    if (!isValidEmail(formData.email)) {
      setErrors(prev => ({ ...prev, email: '이메일 형식이 올바르지 않습니다.' }));
      return;
    }

    try {
      setIsLoading(true);
      const result = await commonService.checkDuplicateValue('email', formData.email);

      if(result.exists){
        setErrors(prev => ({ ...prev, email: '이미 사용 중인 이메일입니다.' }));
        setIsLoading(false);
        setIsEmailChecked(false);
        return;
      }
      toastUtils.success('사용 가능한 이메일입니다.');
      setIsLoading(false);
      setIsEmailChecked(true);
    } catch (error) {
      toastUtils.error('이메일 중복 검사 중 오류가 발생했습니다. 다시 시도해주세요.');
      setIsLoading(false);
      setIsEmailChecked(false);
    }
  };

  /**
   * 폼 유효성 검증
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // 아이디 검증
    const userIdError = validateUserId(formData.userId);
    if (userIdError) {
      newErrors.userId = userIdError;
    }

    // 이메일 검증
    if (!formData.email.trim()) {
      newErrors.email = '이메일을 입력해주세요.';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = '이메일 형식이 올바르지 않습니다.';
    }

    // 비밀번호 검증
    if (!formData.password.trim()) {
      newErrors.password = '비밀번호를 입력해주세요.';
    } else if (!passwordValidation.isValid) {
      newErrors.password = '영문, 숫자, 특수문자 중 2가지 이상 조합 (8자 이상)';
    }

    // 비밀번호 확인 검증
    if (!formData.passwordConfirm.trim()) {
      newErrors.passwordConfirm = '비밀번호 확인을 입력해주세요.';
    } else if (formData.password !== formData.passwordConfirm) {
      newErrors.passwordConfirm = '비밀번호가 일치하지 않습니다.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * 다음 단계로 진행
   */
  const handleNext = () => {
    if (!validateForm()) {
      return;
    }

    onNext(formData);
  };

  return (
    <Container>
      <VerifiedInfoSection>
        <SectionTitle>인증된 정보</SectionTitle>
        <VerifiedInfo>
          <InfoRow>
            <InfoLabel>이름</InfoLabel>
            <InfoValue>{data.name}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>휴대폰</InfoLabel>
            <InfoValue>{data.phone}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>생년월일-성별</InfoLabel>
            <InfoValue>{data.birthGender}</InfoValue>
          </InfoRow>
        </VerifiedInfo>
      </VerifiedInfoSection>

      <FormContainer>
        <FormGroup>
          <Label htmlFor="userId">아이디</Label>
          <InputGroup>
            <Input
              id="userId"
              name="userId"
              type="text"
              placeholder="영문 포함 4-20자 (숫자 사용 가능)"
              value={formData.userId}
              onChange={handleInputChange}
            />
            <CheckButton
              type="button"
              onClick={handleCheckUserId}
              disabled={isLoading || !formData.userId.trim() || !!errors.userId}
              $isChecked={isUserIdChecked}
            >
              {isLoading ? '확인중...' : isUserIdChecked ? '확인완료' : '중복확인'}
            </CheckButton>
          </InputGroup>
          {errors.userId && <ErrorMessage>{errors.userId}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="email">이메일</Label>
          <InputGroup>
            <Input
              id="email"
              name="email"
              type="text"
              placeholder="example@email.com"
              value={formData.email}
              onChange={handleInputChange}
            />
            <CheckButton
              type="button"
              onClick={handleCheckEmail}
              disabled={isLoading || !formData.email.trim() || !!errors.email}
              $isChecked={isEmailChecked}
            >
              {isLoading ? '확인중...' : isEmailChecked ? '확인완료' : '중복확인'}
            </CheckButton>
          </InputGroup>
          {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="password">비밀번호</Label>
          <PasswordInputWrapper>
            <Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="영문, 숫자, 특수문자 중 2가지 이상 조합 (8자 이상)"
              value={formData.password}
              onChange={handleInputChange}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FiEyeOff /> : <FiEye />}
            </PasswordToggle>
          </PasswordInputWrapper>
          {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="passwordConfirm">비밀번호 확인</Label>
          <PasswordInputWrapper>
            <Input
              id="passwordConfirm"
              name="passwordConfirm"
              type={showPasswordConfirm ? 'text' : 'password'}
              placeholder="비밀번호를 다시 입력해주세요"
              value={formData.passwordConfirm}
              onChange={handleInputChange}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
            >
              {showPasswordConfirm ? <FiEyeOff /> : <FiEye />}
            </PasswordToggle>
          </PasswordInputWrapper>
          {errors.passwordConfirm && <ErrorMessage>{errors.passwordConfirm}</ErrorMessage>}
        </FormGroup>
      </FormContainer>

      <ButtonContainer>
        <Button
          $variant="outline"
          $size="md"
          onClick={() => {
            console.log(data);
            console.log(formData);
          }}
        >
          이전
        </Button>
        <Button
          $variant="gradient"
          $size="md"
          disabled={
            !isUserIdChecked || 
            !isEmailChecked || 
            !formData.userId || 
            !formData.email || 
            !formData.password || 
            !formData.passwordConfirm || 
            !!errors.userId || 
            !!errors.email || 
            !!errors.password || 
            !!errors.passwordConfirm ||
            !passwordValidation.isValid
          }
          onClick={handleNext}
        >
          회원가입
        </Button>
      </ButtonContainer>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;


const VerifiedInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const VerifiedInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background-color: ${semanticColors.background.primary};
  border-radius: 6px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InfoLabel = styled.span`
  ${typography.paragraph.p1.default}
  color: ${semanticColors.text.secondary};
`;

const InfoValue = styled.span`
  ${typography.paragraph.p1.default}
  color: ${semanticColors.text.primary};
  font-weight: 500;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  ${typography.paragraph.p1.default}
  color: ${semanticColors.text.primary};
  font-weight: 500;
`;

const InputGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const CheckButton = styled.button<{ $isChecked: boolean }>`
  ${typography.caption.caption1.default}
  padding: 0 16px;
  height: 40px;
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 6px;
  background-color: ${props => props.$isChecked ? semanticColors.background.brand : semanticColors.background.secondary};
  border-color: ${props => props.$isChecked ? semanticColors.background.brand : semanticColors.border.primary};
  color: ${props => props.$isChecked ? semanticColors.text.white : semanticColors.text.primary};
  cursor: pointer;
  white-space: nowrap;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${semanticColors.text.tertiary};
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: ${semanticColors.text.primary};
  }
`;

const ErrorMessage = styled.span`
  ${typography.caption.caption2}
  color: ${semanticColors.background.danger.primary};
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: space-between;
`;

export default FormStep;
